import { useEffect, useRef } from 'react';

/**
 * Lumyn Live WebSocket subscription hook.
 *
 * Opens a WS to `${REACT_APP_BACKEND_URL}/api/v1/live/ws/{sessionId}` and
 * invokes `onEvent(payload)` whenever the server pushes an event.
 *
 * Resilient by design:
 *  - Auto-reconnects on close with capped exponential backoff (1s → 8s).
 *  - Sends `ping` every 25s to keep proxy idle-timeouts off our back.
 *  - Returns nothing: the caller already polls REST as a fallback, so a
 *    silent WS failure degrades gracefully to polling.
 *
 * Phase 2 — surfaces optional `onConnect` / `onDisconnect` so the
 * mobile-first participant page can render a connectivity pip.
 */
export function useLiveSocket(
  sessionId,
  { participantId, onEvent, onConnect, onDisconnect } = {},
) {
  const wsRef = useRef(null);
  const handlerRef = useRef(onEvent);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const closedRef = useRef(false);
  handlerRef.current = onEvent;
  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;

  useEffect(() => {
    if (!sessionId) return undefined;
    closedRef.current = false;
    let attempt = 0;
    let pingTimer = null;
    let reconnectTimer = null;

    const buildUrl = () => {
      const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsBase = httpBase.replace(/^http/, 'ws');
      const qs = participantId ? `?participant_id=${encodeURIComponent(participantId)}` : '';
      return `${wsBase}/api/v1/live/ws/${sessionId}${qs}`;
    };

    const connect = () => {
      if (closedRef.current) return;
      let ws;
      try {
        ws = new WebSocket(buildUrl());
      } catch (err) {
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        if (onConnectRef.current) onConnectRef.current();
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };

      ws.onmessage = (e) => {
        if (e.data === 'pong') return;
        let payload;
        try { payload = JSON.parse(e.data); } catch { return; }
        if (handlerRef.current) handlerRef.current(payload);
      };

      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (onDisconnectRef.current) onDisconnectRef.current();
        if (closedRef.current) return;
        const delay = Math.min(8000, 1000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => { /* close handler will fire next */ };
    };

    connect();
    return () => {
      closedRef.current = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch (_e) { /* noop */ }
    };
  }, [sessionId, participantId]);
}
